import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageSquare, Target, Dumbbell, Users, HelpCircle,
  Bell, BellDot, Activity, MessageCircle, Heart, Eye, Lock, Plus
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Target, Dumbbell, Users, HelpCircle, MessageSquare,
};

const ICON_OPTIONS = [
  { value: 'MessageSquare', label: 'Message' },
  { value: 'Target', label: 'Target' },
  { value: 'Dumbbell', label: 'Training' },
  { value: 'Users', label: 'People' },
  { value: 'HelpCircle', label: 'Help' },
];

function getIcon(name) {
  return ICON_MAP[name] || MessageSquare;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function Forum() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [activity, setActivity] = useState(null);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [showNotifs, setShowNotifs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // New topic (super admin)
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newIcon, setNewIcon] = useState('MessageSquare');
  const [creatingTopic, setCreatingTopic] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  const isLifetime = user?.role === 'super_admin' || user?.subscription_plan === 'lifetime' || user?.plan === 'lifetime';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!isLifetime) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const [catRes, actRes, notifRes] = await Promise.all([
          axios.get(`${API}/api/forum/categories`, { headers }),
          axios.get(`${API}/api/forum/activity`, { headers }),
          axios.get(`${API}/api/forum/notifications`, { headers }),
        ]);
        setCategories(catRes.data.categories);
        setActivity(actRes.data);
        setNotifications(notifRes.data);
      } catch (err) {
        console.error('Forum load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleFollow = async (catId) => {
    try {
      const res = await axios.post(`${API}/api/forum/categories/${catId}/follow`, {}, { headers });
      setCategories(prev => prev.map(c => c.id === catId ? { ...c, following: res.data.following } : c));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/api/forum/notifications/mark-read`, {}, { headers });
      setNotifications(prev => ({ ...prev, unread_count: 0, notifications: prev.notifications.map(n => ({ ...n, read: true })) }));
    } catch (err) {
      console.error(err);
    }
  };

  const createTopic = async () => {
    if (!newName.trim()) return;
    setCreatingTopic(true);
    try {
      const res = await axios.post(`${API}/api/forum/categories`, {
        name: newName.trim(),
        description: newDescription.trim(),
        icon: newIcon,
      }, { headers });
      const cat = res.data.category;
      setCategories(prev => [...prev, { ...cat, following: false }]);
      setShowNewTopic(false);
      setNewName('');
      setNewDescription('');
      setNewIcon('MessageSquare');
      toast.success(`Topic "${cat.name}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add topic');
    } finally {
      setCreatingTopic(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="forum-page">
        {accessDenied ? (
          <div className="text-center py-20" data-testid="forum-locked">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Coaches Forum</h1>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              The Coaches Forum is an exclusive community for Lifetime plan members. Upgrade to connect with other coaches, share ideas and get support.
            </p>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => window.location.href = '/#pricing'}>
              View Lifetime Plan
            </Button>
          </div>
        ) : (
        <>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Coaches Forum</h1>
            <p className="text-sm text-gray-500 mt-1">Connect, share ideas and support each other</p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowNewTopic(true)}
                data-testid="forum-new-topic-btn"
              >
                <Plus className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">New Topic</span>
              </Button>
            )}
            {/* Notifications */}
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNotifs(!showNotifs)}
                className="relative"
                data-testid="forum-notif-btn"
              >
                {notifications.unread_count > 0 ? (
                  <BellDot className="w-4 h-4 text-amber-500" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
                {notifications.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                    {notifications.unread_count}
                  </span>
                )}
              </Button>
              {showNotifs && (
                <div className="absolute right-0 top-10 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border z-50 max-h-[70vh] overflow-y-auto" data-testid="forum-notif-panel">
                  <div className="flex items-center justify-between p-3 border-b">
                    <span className="text-sm font-semibold">Notifications</span>
                    {notifications.unread_count > 0 && (
                      <Button variant="ghost" size="sm" className="text-xs h-6" onClick={markAllRead}>Mark all read</Button>
                    )}
                  </div>
                  {notifications.notifications.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">No notifications yet</p>
                  ) : (
                    notifications.notifications.map(n => (
                      <Link
                        key={n.id}
                        to={`/forum/thread/${n.thread_id}`}
                        onClick={() => setShowNotifs(false)}
                        className={`block px-3 py-2.5 border-b last:border-0 hover:bg-gray-50 ${!n.read ? 'bg-emerald-50/50' : ''}`}
                      >
                        <p className="text-sm">
                          <span className="font-medium">{n.from_user_name}</span>{' '}
                          {n.type === 'reply' && 'replied to your thread'}
                          {n.type === 'mention' && 'mentioned you in'}
                          {n.type === 'new_thread' && 'posted a new thread'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{n.thread_title}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                      </Link>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Activity Stats */}
            {activity && (
              <div className="flex flex-wrap gap-3" data-testid="forum-activity">
                <Badge variant="outline" className="text-sm py-1.5 px-3 gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                  {activity.threads_created} Threads
                </Badge>
                <Badge variant="outline" className="text-sm py-1.5 px-3 gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                  {activity.replies_posted} Replies
                </Badge>
                <Badge variant="outline" className="text-sm py-1.5 px-3 gap-1.5">
                  <Heart className="w-3.5 h-3.5 text-red-500" />
                  {activity.likes_received} Likes Received
                </Badge>
              </div>
            )}

            {/* Categories */}
            <div className="space-y-3">
              {categories.map(cat => {
                const Icon = getIcon(cat.icon);
                return (
                  <Link key={cat.id} to={`/forum/category/${cat.id}`} data-testid={`forum-cat-${cat.id}`}>
                    <Card className="hover:shadow-md transition-shadow border border-gray-100 cursor-pointer">
                      <CardContent className="p-3 sm:p-5 flex items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{cat.name}</h3>
                            {cat.following && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border-0 px-1.5">Following</Badge>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-500 truncate">{cat.description}</p>
                          {/* Mobile-only thread count + last activity */}
                          <div className="flex sm:hidden items-center gap-3 mt-1 text-[11px] text-gray-400">
                            <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{cat.thread_count || 0}</span>
                            {cat.last_thread_at && <span>{timeAgo(cat.last_thread_at)}</span>}
                          </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-4 text-sm text-gray-400 flex-shrink-0">
                          <div className="flex items-center gap-1">
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>{cat.thread_count || 0}</span>
                          </div>
                          {cat.last_thread_at && (
                            <span className="text-xs">{timeAgo(cat.last_thread_at)}</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0 text-xs h-8 w-8 sm:w-auto p-0 sm:px-2"
                          onClick={(e) => { e.preventDefault(); toggleFollow(cat.id); }}
                          data-testid={`follow-${cat.id}`}
                          title={cat.following ? 'Following' : 'Follow'}
                        >
                          {cat.following ? (
                            <><BellDot className="w-4 h-4 sm:mr-1 text-emerald-600" /><span className="hidden sm:inline">Following</span></>
                          ) : (
                            <><Bell className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Follow</span></>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        </>
        )}

        {/* New Topic Dialog (super admin) */}
        <Dialog open={showNewTopic} onOpenChange={setShowNewTopic}>
          <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full">
            <DialogHeader>
              <DialogTitle>Add Forum Topic</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Name</label>
                <Input
                  placeholder="e.g., Nutrition & Recovery"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="new-topic-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Description</label>
                <Textarea
                  placeholder="Short description shown under the topic name"
                  rows={2}
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  data-testid="new-topic-description"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Icon</label>
                <Select value={newIcon} onValueChange={setNewIcon}>
                  <SelectTrigger data-testid="new-topic-icon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(opt => {
                      const Ic = ICON_MAP[opt.value] || MessageSquare;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="flex items-center gap-2">
                            <Ic className="w-4 h-4 text-emerald-600" />
                            {opt.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewTopic(false)}>Cancel</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={createTopic}
                disabled={creatingTopic || !newName.trim()}
                data-testid="submit-new-topic-btn"
              >
                {creatingTopic ? 'Adding...' : 'Add Topic'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
